import { Button as UIButton } from '@headlessui/react';
import clsx from 'clsx';
import { Spinner } from './Spinner';

type ButtonType = 'danger' | 'success' | 'warning' | 'info' | 'normal';

type ButtonProps = {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary';
  btnType?: ButtonType;
  isLoading?: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

const tailwindClass =
  'inline-flex items-center gap-2 rounded-md py-1.5 px-3 text-sm/6 font-semibold shadow-inner shadow-white/10 focus:outline-none data-[focus]:outline-1 data-[focus]:outline-white justify-center transition duration-200';

const primaryClass =
  'bg-cyan-700 hover:bg-cyan-400 focus:outline-blue text-white data-[hover]:bg-cyan-600 data-[open]:bg-cyan-700 text-white';

const secondaryClass = 'bg-transparent hover:bg-cyan-100 text-black border border-cyan-800';

const btnTypeClasses: Record<ButtonType, string> = {
  danger:
    'bg-red-500 enabled:hover:bg-red-400 focus:outline-red text-white data-[hover]:bg-red-400 data-[open]:bg-red-500 text-white',
  success:
    'bg-green-500 enabled:hover:bg-green-400 focus:outline-green text-white data-[hover]:bg-green-400 data-[open]:bg-green-500 text-green-800',
  warning:
    'bg-yellow-500 enabled:hover:bg-yellow-400 focus:outline-yellow text-white data-[hover]:bg-yellow-400 data-[open]:bg-yellow-500 text-yellow-800',
  info: 'bg-blue-500 enabled:hover:bg-blue-400 focus:outline-blue text-white data-[hover]:bg-blue-400 data-[open]:bg-blue-500 text-blue-800',
  normal: ''
};

export const Button = ({
  children,
  variant = 'primary',
  btnType = 'normal',
  isLoading,
  ...props
}: ButtonProps) => {
  return (
    <UIButton
      {...props}
      disabled={isLoading || props.disabled}
      className={clsx(
        tailwindClass,
        variant === 'primary' ? primaryClass : secondaryClass,
        btnTypeClasses[btnType],
        props.className,
        props.disabled && 'opacity-40 pointer-events-none'
      )}>
      <div className={clsx('flex items-center gap-2', isLoading && 'opacity-0')}>{children}</div>
      {isLoading && (
        <div className="absolute">
          <Spinner />
        </div>
      )}
    </UIButton>
  );
};

type AButtonProps = {
  href: string;
  children: React.ReactNode;
  variant?: 'primary' | 'secondary';
  btnType?: ButtonType;
  isLoading?: boolean;
} & React.AnchorHTMLAttributes<HTMLAnchorElement>;

export const AButton = ({
  children,
  href,
  variant = 'primary',
  btnType = 'normal',
  ...props
}: AButtonProps) => {
  return (
    <a
      {...props}
      className={clsx(
        tailwindClass,
        variant === 'primary' ? primaryClass : secondaryClass,
        btnTypeClasses[btnType],
        props.className
      )}
      href={href}>
      {children}
    </a>
  );
};
